import type { ReactNode } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface ChoiceCardOption {
  value: string;
  /**
   * Whatever the choice needs to say — a big icon over a caption, a label with
   * a paragraph explaining it, a logo. The card owns the frame, the selected
   * state and the keyboard handling; what goes inside is yours.
   */
  content: ReactNode;
  /** Shown but unpickable. Prefer this over hiding a choice, so the reader
   *  still learns it exists. */
  disabled?: boolean | undefined;
}

interface ChoiceCardsProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: ChoiceCardOption[];
  /** Force the cards stacked at every width. By default they stack below `md`
   *  and lay out in a row above it, so they don't overflow on a phone. */
  vertical?: boolean | undefined;
  /**
   * Colour of the selected card's highlight. `brand` (default) tints the card
   * blue and turns its text brand; `gold` tints it gold and leaves the text
   * alone (dark), so gold content inside can own the highlight.
   */
  accent?: "brand" | "gold" | undefined;
  "data-testid"?: string | undefined;
}

const checkedClasses: Record<"brand" | "gold", string> = {
  brand:
    "data-[state=checked]:border-brand data-[state=checked]:bg-brand-subtle data-[state=checked]:text-brand-text data-[state=checked]:hover:bg-brand-subtle",
  gold: "data-[state=checked]:border-gold data-[state=checked]:bg-gold/10 data-[state=checked]:hover:bg-gold/10",
};

/**
 * Pick one of a few choices, each presented as a card you click anywhere on.
 * Use it when the choices need room to argue for themselves — an icon, or a
 * sentence of explanation — and `RadioGroup`'s compact dot-and-label row is too
 * small to make the difference clear.
 *
 * The cards are equal width so no choice looks more important than another, and
 * the content is a `ReactNode` so the same component covers "pick the picture
 * that's you" and "read these two options and choose".
 */
export function ChoiceCards({
  value,
  onChange,
  options,
  vertical,
  accent = "brand",
  "data-testid": dataTestid,
}: ChoiceCardsProps) {
  const markDirty = useMarkDialogDirty();

  return (
    <RadioGroupPrimitive.Root
      {...(value !== undefined && { value })}
      onValueChange={(v) => {
        markDirty?.();
        onChange(v);
      }}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      className={`flex gap-4 ${vertical ? "flex-col" : "flex-col md:flex-row md:items-stretch"}`}
    >
      {options.map((opt) => (
        <RadioGroupPrimitive.Item
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled ?? false}
          // flex-1 + basis-0: every card takes an equal share of the row rather
          // than sizing to its own text, so a wordier choice doesn't end up
          // looking like the main one.
          //
          // `group` lets the content react to the card's checked state (e.g. an
          // icon tile that fills in on selection). With the brand accent the
          // checked card also turns its text brand, and Phosphor icons fill with
          // currentColor — so an icon lights up without being told it's
          // selected. Any part that sets its own colour opts out.
          className={`group flex flex-1 basis-0 cursor-pointer items-center justify-center rounded-2xl border border-border-strong bg-card p-6 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 ${checkedClasses[accent]}`}
        >
          {opt.content}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
